import { atom, type WritableAtom } from 'nanostores';

import { type Bet, type BetslipState, type BookieOdds } from '../context/types';

/**
 * Framework-agnostic betslip state core.
 *
 * The non-OC betslip state used to live in seven `useState` hooks inside `BetslipProvider`, which
 * chained it to the React tree. It now lives in these nanostores atoms so the state can be read and
 * written without React — the prerequisite for Astro islands driving the betslip post-migration
 * (NGF-6116). `BetslipProvider` is a thin adapter over this core; the OC repub already proves the
 * target shape via `window.ocBetslip`.
 *
 * One store is created per provider (see `betslip-context.tsx`) rather than as a module singleton, so
 * server-rendered, per-request betslip state stays isolated exactly as it was with `useState`. On the
 * client the single provider publishes its store via `setActiveBetslipStore`, giving non-React
 * consumers a stable handle.
 */
export type BetslipStore = {
  $added: WritableAtom<boolean>;
  $betslip: WritableAtom<Bet[]>;
  $bookieOdds: WritableAtom<BookieOdds[]>;
  $loading: WritableAtom<boolean>;
  $open: WritableAtom<boolean>;
  $selectedBookie: WritableAtom<BookieOdds | null>;
  $stake: WritableAtom<number>;
};

export type BetslipStoreInit = {
  betslip?: Bet[];
  bookieOdds?: BookieOdds[];
  selectedBookie?: BookieOdds | null;
  stake?: number;
};

export const createBetslipStore = (init: BetslipStoreInit = {}): BetslipStore => ({
  $added: atom<boolean>(false),
  $betslip: atom<Bet[]>(init.betslip ?? []),
  $bookieOdds: atom<BookieOdds[]>(init.bookieOdds ?? []),
  $loading: atom<boolean>(false),
  $open: atom<boolean>(false),
  $selectedBookie: atom<BookieOdds | null>(init.selectedBookie ?? null),
  $stake: atom<number>(init.stake ?? 10),
});

/** Plain, non-React snapshot of the whole betslip state — for module/island consumers and tests. */
export const getBetslipSnapshot = (store: BetslipStore): BetslipState => {
  const betslip = store.$betslip.get();
  return {
    added: store.$added.get(),
    betslip,
    betIds: betslip.map(b => b.betId),
    bookieOdds: store.$bookieOdds.get(),
    count: betslip.length,
    loading: store.$loading.get(),
    open: store.$open.get(),
    selectedBookie: store.$selectedBookie.get(),
    stake: store.$stake.get(),
  };
};

// Pointer to the live store on the client, where exactly one BetslipProvider is mounted. Lets non-React
// consumers (Astro islands, a future window surface, diagnostics) subscribe to the core directly.
let activeBetslipStore: BetslipStore | null = null;

export const setActiveBetslipStore = (store: BetslipStore | null): void => {
  // Client-only by contract: the pointer exists for browser consumers, and guarding here keeps any
  // future server-side caller (Astro island, test) from leaking one request's store into another.
  if (typeof window === 'undefined') return;
  activeBetslipStore = store;
};

export const getActiveBetslipStore = (): BetslipStore | null => activeBetslipStore;
